/**
 * Device + session diagnostics for ATO Android.
 *
 * Why this module exists: when AR does not work on a phone, the three
 * plausible causes have completely different fixes, and until now none
 * of them was distinguishable from here.
 *
 *   1. ARCore (Google Play Services for AR) missing or stale → no
 *      immersive-ar session is ever offered.
 *   2. Session starts, but Chrome/ARCore refuses the OPTIONAL features
 *      (plane-detection, depth-sensing, anchors) → the session looks
 *      healthy and finds nothing.
 *   3. Everything granted, tracking never converges → pose frames climb
 *      while every hit test comes back empty.
 *
 * The motivating report (2026-08-24): a Xiaomi 14 Pro, on ARCore's
 * certified list, draws no ground line and finds no planes. Certification
 * covers the device, not the per-feature grant, so (1) and (2) look
 * identical to an operator. Three events, one per stage, make them one
 * PostHog breakdown apart.
 *
 * Everything here is pure: a snapshot in, a flat property bag out. The
 * bags are flat on purpose — PostHog filters and breaks down on
 * top-level scalar properties, not on nested objects.
 *
 * Event NAMES are not defined here. They are pinned to their iOS twins
 * by the telemetry contract (dual-codebase-strategy §11), so the
 * Android-only segmentation rides as a `source` super property instead.
 */

import type { WebXRSessionInfo } from '@/ar/webxr-driver'
import {
  depthYield,
  deriveTrackingHealth,
  type TrackingHealth,
} from '@/ar/tracking-convergence'

/** The segment tag every event from this surface carries.
 *
 *  Segmentation has to be a PROPERTY rather than an event-name prefix:
 *  §11 of the dual-codebase strategy requires event names to match iOS
 *  exactly so cross-platform dashboards can union the two, and a prefix
 *  would silently break every one of them. */
export const ATO_SOURCE = 'ato-android-webxr'

export type AtoEnvironment = 'local' | 'staging' | 'production'

/** Which surface this page is being served from.
 *
 *  Derived from the hostname rather than the build mode so a staging
 *  bundle opened at a local host is still reported as local. An
 *  unrecognised host is reported as `local`, deliberately: guessing
 *  production would push development noise into whatever the production
 *  destination is wired to. */
export function deriveEnvironment(hostname: string): AtoEnvironment {
  const host = hostname.toLowerCase()
  if (host.endsWith('.av.io') || host === 'av.io') return 'local'
  if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') {
    return 'local'
  }
  if (host.startsWith('staging.')) return 'staging'
  if (host.endsWith('.autoviz.io')) return 'production'
  return 'local'
}

/** Properties attached to every subsequent event via `register`. */
export function buildSuperProperties({
  hostname,
}: {
  hostname: string
}): Record<string, unknown> {
  return {
    source: ATO_SOURCE,
    // Kept at the iOS-parity value so cross-platform dashboards still
    // union the two surfaces; `source` is the additional segment.
    platform: 'android_webar',
    sdk_version: 'webxr_native',
    environment: deriveEnvironment(hostname),
  }
}

/** Values only obtainable from the async `userAgentData` client hints.
 *  Absent on browsers without the API, and on any request where the
 *  hint was refused. */
export interface HighEntropyHints {
  model?: string
  platformVersion?: string
}

interface CapabilityProbeInput {
  userAgent: string
  highEntropy?: HighEntropyHints
  webxrApiPresent: boolean
  immersiveArSupported: boolean
  secureContext: boolean
  webgl2: boolean
  deviceMemory: number | null
  hardwareConcurrency: number | null
  screenWidth: number
  screenHeight: number
  devicePixelRatio: number
}

/** Why this device cannot run AR, in the order the causes gate. */
function deriveUnsupportedReason(input: CapabilityProbeInput): string | null {
  if (input.immersiveArSupported) return null
  // WebXR is unconditionally unavailable outside a secure context, so
  // this has to be checked before concluding anything about the browser.
  if (!input.secureContext) return 'insecure_context'
  if (!input.webxrApiPresent) return 'no_webxr_api'
  if (!input.webgl2) return 'no_webgl2'
  // WebXR is present and the context is fine, but the runtime says
  // immersive-ar is unavailable. On Android that is overwhelmingly a
  // missing or stale "Google Play Services for AR" — an operator-
  // actionable fix, which is why it gets its own reason.
  return 'arcore_unavailable'
}

const ANDROID_UA = /Android\s+([\d.]+);\s*([^;)]+?)(?:\s+Build\/[^;)]*)?\)/
const CHROME_UA = /Chrome\/([\d.]+)/

/** Chrome's User-Agent Reduction substitutes a frozen OS version and a
 *  placeholder device model into the UA of every Android device.
 *
 *  Melih's HUD read "Android 10 · Chrome/151.0.0.0" on a phone running
 *  neither (2026-08-24). Parsing those values does not merely lose
 *  detail, it actively misleads: every modern Android device reports
 *  the same non-existent one, and device model is precisely the field
 *  needed to tell one operator's phone from another's. */
const REDUCED_UA = /Android\s+10;\s*K\)/

/** Trims a client hint down to a value, or null. Browsers return "" for
 *  a hint they will not answer, which must not read as an answer. */
function hintOrNull(value: string | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed == null || trimmed === '' ? null : trimmed
}

/**
 * Boot-time probe, fired BEFORE Start AR is offered.
 *
 * This is the only event a device in failure mode (1) will ever send —
 * no session means no session events — so it has to stand alone as a
 * complete diagnosis rather than as context for a later event.
 */
export function buildCapabilityProbe(
  input: CapabilityProbeInput,
): Record<string, unknown> {
  const android = ANDROID_UA.exec(input.userAgent)
  const chrome = CHROME_UA.exec(input.userAgent)
  const uaReduced = REDUCED_UA.test(input.userAgent)
  // Client hints first; they are the only trustworthy source once the
  // UA is reduced. Fall back to UA parsing only when the UA is real.
  const model =
    hintOrNull(input.highEntropy?.model) ??
    (uaReduced ? null : android?.[2]?.trim() ?? null)
  const androidVersion =
    hintOrNull(input.highEntropy?.platformVersion) ??
    (uaReduced ? null : android?.[1] ?? null)
  return {
    ua_reduced: uaReduced,
    webxr_api_present: input.webxrApiPresent,
    immersive_ar_supported: input.immersiveArSupported,
    unsupported_reason: deriveUnsupportedReason(input),
    secure_context: input.secureContext,
    webgl2: input.webgl2,
    device_model: model,
    android_version: androidVersion,
    chrome_version: chrome?.[1] ?? null,
    device_memory_gb: input.deviceMemory,
    hardware_concurrency: input.hardwareConcurrency,
    screen_width: input.screenWidth,
    screen_height: input.screenHeight,
    device_pixel_ratio: input.devicePixelRatio,
    user_agent: input.userAgent,
  }
}

/** The optional features whose refusal is indistinguishable from a
 *  healthy session without this breakdown. Ordered as the driver
 *  requests them. */
const TRACKED_FEATURES = [
  ['feature_hit_test', 'hit-test'],
  ['feature_dom_overlay', 'dom-overlay'],
  ['feature_plane_detection', 'plane-detection'],
  ['feature_depth_sensing', 'depth-sensing'],
  ['feature_anchors', 'anchors'],
  ['feature_light_estimation', 'light-estimation'],
  ['feature_camera_access', 'camera-access'],
] as const

/**
 * Fired once, immediately after `requestSession` resolves.
 *
 * One boolean per feature rather than only the raw list: PostHog breaks
 * down on scalar properties, so "every session that got no plane
 * detection" is a click here and a contains-match otherwise. The raw
 * list rides along too, so a grant we never thought to track is still
 * visible.
 */
export function buildSessionStartProperties(
  sessionInfo: Pick<WebXRSessionInfo, 'enabledFeatures' | 'depthStatus'>,
): Record<string, unknown> {
  const granted = new Set(sessionInfo.enabledFeatures ?? [])
  const flags: Record<string, unknown> = {}
  for (const [key, feature] of TRACKED_FEATURES) {
    flags[key] = granted.has(feature)
  }
  return {
    ...flags,
    // Sorted so the same grant set is the same string every time and
    // groups cleanly in a breakdown.
    enabled_features: [...granted].sort().join(','),
    depth_requested: sessionInfo.depthStatus.requested,
    depth_usage: sessionInfo.depthStatus.depthUsage,
    depth_data_format: sessionInfo.depthStatus.depthDataFormat,
  }
}

/**
 * Fired once, when the session ends.
 *
 * `found_any_surface` is the load-bearing one: a session where pose
 * frames climbed but no hit test ever landed is failure mode (3), and
 * it looks nothing like a session that never started.
 */
export function buildSessionSummaryProperties(
  sessionInfo: Pick<
    WebXRSessionInfo,
    | 'elapsedMs'
    | 'viewerPoseFrames'
    | 'hitTestFramesWithResult'
    | 'hitTestFramesEmpty'
    | 'detectedPlanesType'
    | 'currentSurface'
    | 'currentSurfaceNormalY'
    | 'depthStatus'
    | 'anchorStatus'
  >,
  outcome: {
    wheelsPlaced: number
    floorFound: boolean
    planeCount: number
    trackingHealth: TrackingHealth
  },
): Record<string, unknown> {
  return {
    tracking_health: outcome.trackingHealth,
    plane_count: outcome.planeCount,
    depth_yield: depthYield({
      depthFramesWith: sessionInfo.depthStatus.framesWithDepth,
      depthFramesWithout: sessionInfo.depthStatus.framesWithoutDepth,
    }),
    session_duration_s: Math.round(sessionInfo.elapsedMs / 1000),
    viewer_pose_frames: sessionInfo.viewerPoseFrames,
    hit_test_frames_with_result: sessionInfo.hitTestFramesWithResult,
    hit_test_frames_empty: sessionInfo.hitTestFramesEmpty,
    found_any_surface: sessionInfo.hitTestFramesWithResult > 0,
    detected_planes_type: sessionInfo.detectedPlanesType,
    current_surface: sessionInfo.currentSurface,
    current_surface_normal_y: sessionInfo.currentSurfaceNormalY,
    // Anchor counters, so the report stands in for the on-device HUD.
    // "created 1 / landed 0" (the anchor never attached, wheel is on
    // unanchored coordinates) and "created 1 / landed 1, drift
    // climbing" (attached, ARCore is moving it) are different bugs with
    // different fixes, and nothing else here tells them apart.
    anchors_granted: sessionInfo.anchorStatus.featureGranted,
    anchors_created: sessionInfo.anchorStatus.created,
    anchors_landed: sessionInfo.anchorStatus.landed,
    anchors_rejected: sessionInfo.anchorStatus.rejected,
    anchor_pose_lost_frames: sessionInfo.anchorStatus.poseLostFrames,
    anchor_drift_m: sessionInfo.anchorStatus.firstAnchorDriftMeters,
    // The D / P / delta readout that identified the Xiaomi's depth map
    // as 3m long. It rode only on the degraded alarm, so a session that
    // never tripped the alarm lost it.
    depth_last_m: sessionInfo.depthStatus.lastDepthM,
    plane_hit_last_m: sessionInfo.depthStatus.lastPlaneHitDistanceM,
    depth_plane_delta_m: sessionInfo.depthStatus.lastDeltaM,
    depth_frames_with: sessionInfo.depthStatus.framesWithDepth,
    depth_frames_without: sessionInfo.depthStatus.framesWithoutDepth,
    depth_last_failure: sessionInfo.depthStatus.lastFailure,
    floor_found: outcome.floorFound,
    wheels_placed: outcome.wheelsPlaced,
  }
}

/**
 * Fired once per session, the moment tracking is judged degraded.
 *
 * Its own event rather than a field on the summary because the summary
 * only arrives when the session ENDS, and a user whose wheels are
 * floating away tends to close the tab rather than exit cleanly — the
 * summary may never be sent. This one fires while the session is live.
 */
export function buildTrackingDegradedProperties(
  sessionInfo: Pick<
    WebXRSessionInfo,
    | 'elapsedMs'
    | 'viewerPoseFrames'
    | 'hitTestFramesWithResult'
    | 'hitTestFramesEmpty'
    | 'depthStatus'
  >,
  planeCount: number,
): Record<string, unknown> {
  return {
    seconds_to_verdict: Math.round(sessionInfo.elapsedMs / 1000),
    plane_count: planeCount,
    viewer_pose_frames: sessionInfo.viewerPoseFrames,
    // Both counters ride along because their COMBINATION is the
    // diagnosis: hit tests landing while no plane is ever detected is
    // Chrome's estimated-surface fallback, not a mapped scene.
    hit_test_frames_with_result: sessionInfo.hitTestFramesWithResult,
    hit_test_frames_empty: sessionInfo.hitTestFramesEmpty,
    depth_granted: sessionInfo.depthStatus.featureGranted,
    depth_yield: depthYield({
      depthFramesWith: sessionInfo.depthStatus.framesWithDepth,
      depthFramesWithout: sessionInfo.depthStatus.framesWithoutDepth,
    }),
    depth_last_failure: sessionInfo.depthStatus.lastFailure,
    depth_last_m: sessionInfo.depthStatus.lastDepthM,
    plane_hit_last_m: sessionInfo.depthStatus.lastPlaneHitDistanceM,
    depth_plane_delta_m: sessionInfo.depthStatus.lastDeltaM,
  }
}

/** Health verdict from a live session snapshot. */
export function trackingHealthFor(
  sessionInfo: Pick<WebXRSessionInfo, 'elapsedMs' | 'enabledFeatures' | 'depthStatus'>,
  planeCount: number,
): TrackingHealth {
  return deriveTrackingHealth({
    elapsedMs: sessionInfo.elapsedMs,
    planeDetectionGranted: (sessionInfo.enabledFeatures ?? []).includes(
      'plane-detection',
    ),
    planeCount,
    depthGranted: sessionInfo.depthStatus.featureGranted,
    depthFramesWith: sessionInfo.depthStatus.framesWithDepth,
    depthFramesWithout: sessionInfo.depthStatus.framesWithoutDepth,
  })
}
